import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FaTimes,
  FaRocket,
  FaCode,
  FaLaptopCode,
  FaCheckCircle,
  FaArrowRight,
  FaGraduationCap,
} from "react-icons/fa";
import { SiGo } from "react-icons/si";
import { STORAGE_KEYS } from "../../config/storageKeys";

// 2 hours in milliseconds
const AD_COOLDOWN_MS = 2 * 60 * 60 * 1000;

// Golang Bootcamp course ID - update this to match your actual course ID
// eslint-disable-next-line no-unused-vars
const GOLANG_BOOTCAMP_COURSE_ID = "golang-backend-bootcamp";

export default function BootcampPromoModal() {
  const [show, setShow] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if we should show the ad
    const checkAndShowAd = () => {
      try {
        const dismissedData = localStorage.getItem(STORAGE_KEYS.BOOTCAMP_AD_DISMISSED);

        if (dismissedData) {
          const { timestamp } = JSON.parse(dismissedData);
          const now = Date.now();

          // If less than 2 hours have passed, don't show
          if (now - timestamp < AD_COOLDOWN_MS) {
            return;
          }
        }

        setShow(true);
        return
      } catch (error) {
        // If there's any error reading storage, show the ad
        setShow(true);
      }
    };

    checkAndShowAd();
  }, []);

  const handleDismiss = () => {
    // Record dismissal time
    localStorage.setItem(
      STORAGE_KEYS.BOOTCAMP_AD_DISMISSED,
      JSON.stringify({ timestamp: Date.now() })
    );
    setShow(false);
  };

  const handleExploreCourse = () => {
    // Record interaction time
    localStorage.setItem(
      STORAGE_KEYS.BOOTCAMP_AD_DISMISSED,
      JSON.stringify({ timestamp: Date.now() })
    );
    setShow(false);
    navigate(`/courses`);
  };

  const features = [
    { icon: FaCode, text: "Start from \"Hello World\" basics" },
    { icon: FaLaptopCode, text: "Real assessment-style environment" },
    { icon: FaCheckCircle, text: "Test-case based coding problems" },
    { icon: FaGraduationCap, text: "MNC hiring pattern focused" },
  ];

  return (
    <AnimatePresence>
      {show && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/70 dark:bg-black/80 backdrop-blur-sm z-50 p-3 sm:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 rounded-2xl sm:rounded-3xl shadow-2xl max-w-md sm:max-w-lg w-full relative overflow-hidden border border-slate-700/50 dark:border-gray-600/50 my-auto max-h-[95vh] sm:max-h-[90vh] flex flex-col"
          >
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-0 right-0 w-40 sm:w-64 h-40 sm:h-64 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/3 animate-pulse"></div>
              <div className="absolute bottom-0 left-0 w-32 sm:w-48 h-32 sm:h-48 bg-gradient-to-br from-teal-500/20 to-emerald-500/20 rounded-full filter blur-3xl translate-y-1/2 -translate-x-1/3 animate-pulse"></div>
            </div>

            {/* Close button - always visible */}
            <button
              onClick={handleDismiss}
              className="absolute top-2 right-2 sm:top-3 sm:right-3 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full text-slate-400 hover:text-white bg-slate-800/80 hover:bg-slate-700 transition-all duration-300 z-20 border border-slate-600/50"
              aria-label="Close"
            >
              <FaTimes className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>

            {/* Content - scrollable */}
            <div className="relative z-10 p-4 sm:p-6 overflow-y-auto flex-1">
              {/* Header with Go logo */}
              <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-5 pr-8">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-cyan-500/30 relative flex-shrink-0">
                  <SiGo className="w-7 h-7 sm:w-9 sm:h-9 text-white" />
                  <div className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-gradient-to-r from-orange-400 to-red-500 rounded-full border-2 border-slate-900 flex items-center justify-center">
                    <span className="text-[6px] sm:text-[8px] font-bold text-white">NEW</span>
                  </div>
                </div>
                <div className="min-w-0">
                  <p className="text-cyan-400 text-xs sm:text-sm font-semibold tracking-wide uppercase">
                    🚀 Just Launched
                  </p>
                  <h2 className="text-lg sm:text-xl font-bold text-white leading-tight">
                    Golang Backend Bootcamp
                  </h2>
                </div>
              </div>

              {/* Target audience badges */}
              <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-4 sm:mb-5">
                <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-cyan-500/20 text-cyan-300 text-[10px] sm:text-xs font-medium rounded-full border border-cyan-500/30">
                  👨‍🎓 Freshers
                </span>
                <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-teal-500/20 text-teal-300 text-[10px] sm:text-xs font-medium rounded-full border border-teal-500/30">
                  🔄 Switchers
                </span>
                <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-emerald-500/20 text-emerald-300 text-[10px] sm:text-xs font-medium rounded-full border border-emerald-500/30">
                  📈 2-3 Yrs Exp
                </span>
              </div>

              {/* Description */}
              <p className="text-slate-300 dark:text-gray-300 text-xs sm:text-sm mb-4 sm:mb-5 leading-relaxed">
                Master backend development with Go! For{" "}
                <span className="text-cyan-400 font-medium">off-campus drives</span>,{" "}
                <span className="text-teal-400 font-medium">MNC assessments</span>, and backend role switches.
              </p>

              {/* Features grid - 2 columns on mobile too */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4 sm:mb-5">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * index }}
                    className="flex items-center gap-2 sm:gap-3 bg-slate-800/50 dark:bg-gray-800/50 rounded-lg sm:rounded-xl px-2 sm:px-3 py-2 sm:py-2.5 border border-slate-700/50 dark:border-gray-600/50"
                  >
                    <div className="w-6 h-6 sm:w-7 sm:h-7 bg-gradient-to-br from-cyan-500/20 to-teal-500/20 rounded-md sm:rounded-lg flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-cyan-400" />
                    </div>
                    <span className="text-slate-200 dark:text-gray-200 text-[10px] sm:text-xs font-medium leading-tight">{feature.text}</span>
                  </motion.div>
                ))}
              </div>

              {/* Highlight box */}
              <div className="bg-gradient-to-r from-cyan-500/10 to-teal-500/10 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-4 sm:mb-5 border border-cyan-500/20">
                <div className="flex items-start gap-2 sm:gap-3">
                  <FaRocket className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <p className="text-slate-300 dark:text-gray-300 text-xs sm:text-sm">
                    <span className="text-white font-semibold">Hands-on practice!</span>{" "}
                    Real assessment environment used by MNCs.
                  </p>
                </div>
              </div>

              {/* CTA Buttons - always visible */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button
                  onClick={handleExploreCourse}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base text-slate-900 bg-gradient-to-r from-cyan-400 to-teal-400 hover:from-cyan-300 hover:to-teal-300 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300 active:scale-[0.98] group"
                >
                  <FaArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:translate-x-0.5 transition-transform" />
                  Explore Course
                </button>
                <button
                  onClick={handleDismiss}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base text-slate-300 dark:text-gray-300 bg-slate-800/80 dark:bg-gray-700/80 border border-slate-600/50 dark:border-gray-500/50 hover:border-slate-500 hover:text-white hover:bg-slate-700/80 dark:hover:bg-gray-600/80 transition-all duration-300 active:scale-[0.98]"
                >
                  Maybe Later
                </button>
              </div>
            </div>

            {/* Bottom decorative bar */}
            <div className="h-1 bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 flex-shrink-0"></div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
